/**
 * CouponLadderEditor — base-pricing coupon ladder per program (RTL / GUC).
 *
 * Ported from digital_sizer/components/admin/CouponLadderEditor.tsx into
 * eastviewos: same /admin/coupon-ladders read/write + bulk-shift + lock +
 * audit-log history, but recreated in MUI to the eastviewos aesthetic (plain
 * editable table instead of AG Grid) with react-query swapped for plain
 * useState/useEffect via adminPricingApi. The before/after review is a simple
 * diff table (no diff lib).
 */
import { useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import AddRounded from "@mui/icons-material/AddRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import LockOutlined from "@mui/icons-material/LockOutlined";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import ChangeHistoryDrawer from "../../components/admin/ChangeHistoryDrawer";
import ProgramToggle from "../GridsScreen/ProgramToggle";
import { adminPricingApi } from "../../api/adminPricingApi";
import { teal } from "../../theme/colors";

const PROGRAMS = ["RTL", "GUC"];
const PRICE_COLS = ["fixed_30", "arm_5_1", "arm_7_1"];
const COL_LABELS = {
  fixed_30: "30-yr Fixed",
  arm_5_1: "5/1 ARM",
  arm_7_1: "7/1 ARM",
};

function fmtCoupon(v) {
  if (v === null || v === undefined || v === "") return "";
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return "";
  return (n * 100).toFixed(4) + "%";
}

function fmtPrice(v) {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return "";
  return n.toFixed(3);
}

function rowsEqual(a, b) {
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (
      Math.abs(a[i].coupon - b[i].coupon) > 1e-9 ||
      Math.abs(a[i].fixed_30 - b[i].fixed_30) > 1e-6 ||
      Math.abs(a[i].arm_5_1 - b[i].arm_5_1) > 1e-6 ||
      Math.abs(a[i].arm_7_1 - b[i].arm_7_1) > 1e-6
    ) {
      return false;
    }
  }
  return true;
}

function computeDiff(before, after) {
  const byCoupon = new Map();
  const key = (c) => Number(c).toFixed(6);
  for (const r of before || []) byCoupon.set(key(r.coupon), { b: r });
  for (const r of after || []) {
    const k = key(r.coupon);
    const cur = byCoupon.get(k) || {};
    cur.a = r;
    byCoupon.set(k, cur);
  }
  const out = [];
  const couponKeys = Array.from(byCoupon.keys()).sort((x, y) => Number(x) - Number(y));
  for (const k of couponKeys) {
    const { b, a } = byCoupon.get(k);
    if (b && !a) {
      out.push({ kind: "removed", coupon: b.coupon, before: b, changes: {} });
    } else if (!b && a) {
      out.push({ kind: "added", coupon: a.coupon, after: a, changes: {} });
    } else if (b && a) {
      const changes = {};
      for (const col of PRICE_COLS) {
        const bv = Number(b[col]);
        const av = Number(a[col]);
        if (Math.abs(bv - av) > 1e-6) changes[col] = [bv, av];
      }
      out.push({
        kind: Object.keys(changes).length ? "modified" : "unchanged",
        coupon: a.coupon,
        before: b,
        after: a,
        changes,
      });
    }
  }
  return out;
}

// ── Bulk shift bar ───────────────────────────────────────────────────────────
function BulkShiftBar({ onApplyCoupon, onApplyPrice, busy }) {
  const [couponVal, setCouponVal] = useState("");
  const [couponSign, setCouponSign] = useState(1);
  const [priceVal, setPriceVal] = useState("");
  const [priceSign, setPriceSign] = useState(1);

  const applyCoupon = () => {
    const n = Number(couponVal);
    if (!Number.isFinite(n) || n === 0) return;
    onApplyCoupon(Math.trunc(n) * couponSign);
  };
  const applyPrice = () => {
    const n = Number(priceVal);
    if (!Number.isFinite(n) || n === 0) return;
    onApplyPrice(Math.trunc(n) * priceSign);
  };

  return (
    <Flx wrap g={2} sx={{ alignItems: "flex-end" }}>
      <Flx ac g={1}>
        <TextField
          size="small"
          label="Coupon shift (bps)"
          type="number"
          value={couponVal}
          onChange={(e) => setCouponVal(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: 0 }}
          sx={{ width: 150 }}
        />
        <Button size="small" variant={couponSign === -1 ? "contained" : "outlined"} onClick={() => setCouponSign(-1)} sx={{ minWidth: 36 }}>
          −
        </Button>
        <Button size="small" variant={couponSign === 1 ? "contained" : "outlined"} onClick={() => setCouponSign(1)} sx={{ minWidth: 36 }}>
          +
        </Button>
        <Button size="small" variant="contained" disableElevation onClick={applyCoupon} disabled={busy} sx={{ textTransform: "none" }}>
          Apply
        </Button>
      </Flx>
      <Tooltip title="Shifts all price columns by N/100 price points uniformly across all coupons.">
        <Flx ac g={1}>
          <TextField
            size="small"
            label="Price shift (bps · 1/100)"
            type="number"
            value={priceVal}
            onChange={(e) => setPriceVal(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: 0 }}
            sx={{ width: 190 }}
          />
          <Button size="small" variant={priceSign === -1 ? "contained" : "outlined"} onClick={() => setPriceSign(-1)} sx={{ minWidth: 36 }}>
            −
          </Button>
          <Button size="small" variant={priceSign === 1 ? "contained" : "outlined"} onClick={() => setPriceSign(1)} sx={{ minWidth: 36 }}>
            +
          </Button>
          <Button size="small" variant="contained" disableElevation onClick={applyPrice} disabled={busy} sx={{ textTransform: "none" }}>
            Apply
          </Button>
        </Flx>
      </Tooltip>
    </Flx>
  );
}

// ── Diff (review) modal ────────────────────────────────────────────────────
function DiffModal({ open, before, after, onConfirm, onClose, saving }) {
  const diff = useMemo(() => (open ? computeDiff(before, after) : []), [open, before, after]);
  const modifiedCount = diff.filter((d) => d.kind !== "unchanged").length;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
        Review changes ({modifiedCount} {modifiedCount === 1 ? "row" : "rows"} different)
      </DialogTitle>
      <DialogContent dividers>
        {diff.length === 0 ? (
          <Txt sx={{ fontSize: 13, color: "#94a3b8" }}>No differences.</Txt>
        ) : (
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <Box component="thead" sx={{ "& th": { textAlign: "right", p: "6px 8px", color: "#64748b", fontWeight: 700, fontSize: 11 }, "& th:first-of-type": { textAlign: "left" } }}>
              <tr>
                <th>Coupon</th>
                {PRICE_COLS.map((c) => (
                  <th key={c}>{COL_LABELS[c]}</th>
                ))}
              </tr>
            </Box>
            <tbody>
              {diff.map((d) => {
                const bg =
                  d.kind === "added"
                    ? "rgba(4,120,87,0.08)"
                    : d.kind === "removed"
                      ? "rgba(220,38,38,0.08)"
                      : "transparent";
                return (
                  <tr key={d.coupon} style={{ background: bg }}>
                    <td style={{ padding: "6px 8px", fontVariantNumeric: "tabular-nums" }}>{fmtCoupon(d.coupon)}</td>
                    {PRICE_COLS.map((c) => {
                      const change = d.changes[c];
                      const cellBg = change ? "rgba(20,80,107,0.10)" : "transparent";
                      const val = d.after ? d.after[c] : d.before ? d.before[c] : null;
                      return (
                        <td key={c} style={{ textAlign: "right", padding: "6px 8px", background: cellBg, fontVariantNumeric: "tabular-nums" }}>
                          {change ? (
                            <span>
                              <s style={{ color: "#dc2626" }}>{fmtPrice(change[0])}</s> → <b>{fmtPrice(change[1])}</b>
                            </span>
                          ) : val != null ? (
                            fmtPrice(val)
                          ) : (
                            "—"
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button variant="contained" disableElevation onClick={onConfirm} disabled={saving || modifiedCount === 0} sx={{ textTransform: "none" }}>
          {saving ? "Saving…" : "Confirm & save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Lock dialog ──────────────────────────────────────────────────────────────
function LockDialog({ open, onClose, onSubmit, saving, version }) {
  const [note, setNote] = useState("");
  useEffect(() => {
    if (!open) setNote("");
  }, [open]);
  const trimmed = note.trim();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>Lock version v{version}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          required
          label="Note"
          placeholder="Why are you locking this version?"
          multiline
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button variant="contained" disableElevation onClick={() => onSubmit(trimmed)} disabled={!trimmed || saving} sx={{ textTransform: "none" }}>
          {saving ? "Locking…" : "Lock"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function CouponLadderEditor() {
  const [program, setProgram] = useState("RTL");
  const [doc, setDoc] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [diffOpen, setDiffOpen] = useState(false);
  const [lockOpen, setLockOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = (prog) => {
    setLoading(true);
    return adminPricingApi
      .getCouponLadder(prog)
      .then((d) => {
        setDoc(d);
        setDraft((d.rows || []).map((r) => ({ ...r })));
      })
      .catch(() => {
        setDoc(null);
        setDraft(null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(program);
  }, [program]);

  const dirty = useMemo(() => {
    if (!doc || !draft) return false;
    return !rowsEqual(doc.rows, draft);
  }, [doc, draft]);

  const lockBlocked = doc ? doc.version === doc.locked_version : true;

  const setCell = (idx, field, raw) => {
    setDraft((cur) => {
      if (!cur) return cur;
      const next = cur.slice();
      let v;
      if (field === "coupon") {
        const n = Number(raw);
        v = n > 1 ? n / 100 : n;
      } else {
        v = Number(raw);
      }
      next[idx] = { ...next[idx], [field]: Number.isFinite(v) ? v : next[idx][field] };
      return next;
    });
  };

  const addRow = () =>
    setDraft((cur) => {
      const base = { coupon: 0.06, fixed_30: 100, arm_5_1: 100, arm_7_1: 100 };
      return cur ? [...cur, base] : [base];
    });

  const deleteRow = (idx) => setDraft((cur) => (cur ? cur.filter((_, i) => i !== idx) : cur));

  const onConfirmSave = () => {
    if (!draft) return;
    setSaving(true);
    adminPricingApi
      .saveCouponLadder(program, draft)
      .then(() => {
        setDiffOpen(false);
        return load(program);
      })
      .catch((e) => window.alert(`Save failed: ${e?.response?.data?.detail || e?.message || e}`))
      .finally(() => setSaving(false));
  };

  const onBulkCoupon = (bps) => {
    setBusy(true);
    adminPricingApi
      .bulkShiftCouponLadder(program, { axis: "coupon", delta_bps: bps })
      .then(() => load(program))
      .catch((e) => window.alert(`Shift failed: ${e?.response?.data?.detail || e?.message || e}`))
      .finally(() => setBusy(false));
  };

  const onBulkPrice = (bps) => {
    setBusy(true);
    adminPricingApi
      .bulkShiftCouponLadder(program, { axis: "price", delta_bps: bps, columns: PRICE_COLS })
      .then(() => load(program))
      .catch((e) => window.alert(`Shift failed: ${e?.response?.data?.detail || e?.message || e}`))
      .finally(() => setBusy(false));
  };

  const onLock = (note) => {
    setBusy(true);
    adminPricingApi
      .lockCouponLadder(program, note)
      .then(() => {
        setLockOpen(false);
        return load(program);
      })
      .catch((e) => window.alert(`Lock failed: ${e?.response?.data?.detail || e?.message || e}`))
      .finally(() => setBusy(false));
  };

  return (
    <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 3.5, bgcolor: "#fff", overflow: "hidden" }}>
      <Flx ac jb g={1} sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid #eef2f5", flexWrap: "wrap" }}>
        <Flx ac g={1.5}>
          <Txt sx={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>Base Pricing — Coupon Ladder</Txt>
          <ProgramToggle value={program} options={PROGRAMS} onChange={setProgram} />
        </Flx>
        <Flx ac g={1} sx={{ flexWrap: "wrap" }}>
          {doc && (
            <Txt sx={{ fontSize: 11.5, color: "#64748b" }}>
              v{doc.version} · locked v{doc.locked_version}
              {doc.locked_by ? ` by ${doc.locked_by}` : ""}
            </Txt>
          )}
          <Tooltip title={lockBlocked ? "Save changes first" : "Pin the current version as the live one"}>
            <span>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<LockOutlined sx={{ fontSize: 16 }} />}
                onClick={() => setLockOpen(true)}
                disabled={lockBlocked || dirty || busy}
                sx={{ textTransform: "none" }}
              >
                Lock version
              </Button>
            </span>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            startIcon={<HistoryRounded sx={{ fontSize: 16 }} />}
            onClick={() => setHistoryOpen(true)}
            sx={{ textTransform: "none" }}
          >
            History
          </Button>
        </Flx>
      </Flx>

      <Box sx={{ p: 2.5 }}>
        {loading || !doc || !draft ? (
          <Flx center sx={{ py: 5 }}>
            <CircularProgress size={20} sx={{ color: teal[700] }} />
          </Flx>
        ) : (
          <Flx column g={2}>
            <BulkShiftBar busy={busy} onApplyCoupon={onBulkCoupon} onApplyPrice={onBulkPrice} />

            <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, overflow: "hidden" }}>
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                <Box
                  component="thead"
                  sx={{
                    "& th": {
                      bgcolor: "#f1f6f9",
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#475569",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      py: 1,
                      px: 1.5,
                      borderBottom: "1px solid #e5e7eb",
                      textAlign: "right",
                    },
                    "& th:first-of-type": { textAlign: "left" },
                    "& th:last-of-type": { width: 48 },
                  }}
                >
                  <Box component="tr">
                    <Box component="th">Coupon (%)</Box>
                    {PRICE_COLS.map((c) => (
                      <Box component="th" key={c}>
                        {COL_LABELS[c]}
                      </Box>
                    ))}
                    <Box component="th" />
                  </Box>
                </Box>
                <Box
                  component="tbody"
                  sx={{
                    "& td": { py: 0.5, px: 1.5, borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" },
                    "& tr:last-of-type td": { borderBottom: "none" },
                    "& tr:nth-of-type(even)": { bgcolor: "#fcfcfd" },
                    "& tr:hover": { bgcolor: "#f8fafc" },
                  }}
                >
                  {draft.map((r, i) => (
                    <Box component="tr" key={i}>
                      <Box component="td">
                        <TextField
                          type="number"
                          size="small"
                          defaultValue={r.coupon != null ? (r.coupon * 100).toFixed(4) : ""}
                          key={`c-${i}-${r.coupon}`}
                          onBlur={(e) => setCell(i, "coupon", e.target.value)}
                          inputProps={{ step: 0.125, style: { textAlign: "left" } }}
                          sx={{ width: 110, "& input": { py: 0.5, fontSize: 13, fontVariantNumeric: "tabular-nums" } }}
                        />
                      </Box>
                      {PRICE_COLS.map((c) => (
                        <Box component="td" key={c} sx={{ textAlign: "right" }}>
                          <TextField
                            type="number"
                            size="small"
                            value={r[c] ?? ""}
                            onChange={(e) => setCell(i, c, e.target.value)}
                            inputProps={{ step: 0.001, style: { textAlign: "right" } }}
                            sx={{ width: 110, "& input": { py: 0.5, fontSize: 13, fontVariantNumeric: "tabular-nums" } }}
                          />
                        </Box>
                      ))}
                      <Box component="td" sx={{ textAlign: "center" }}>
                        <IconButton size="small" onClick={() => deleteRow(i)}>
                          <DeleteOutlineRounded sx={{ fontSize: 18, color: "#94a3b8" }} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            <Flx ac g={1.5}>
              <Button size="small" variant="text" startIcon={<AddRounded sx={{ fontSize: 16 }} />} onClick={addRow} sx={{ textTransform: "none" }}>
                Add row
              </Button>
              {dirty && <Chip label="Unsaved changes" color="warning" size="small" sx={{ height: 20, fontSize: 11 }} />}
              <Box sx={{ flex: 1 }} />
              {dirty && (
                <>
                  <Button variant="text" onClick={() => setDraft(doc.rows.map((r) => ({ ...r })))} sx={{ textTransform: "none" }}>
                    Discard
                  </Button>
                  <Button variant="contained" disableElevation onClick={() => setDiffOpen(true)} disabled={saving} sx={{ textTransform: "none", fontWeight: 600 }}>
                    {saving ? "Saving…" : "Save changes"}
                  </Button>
                </>
              )}
            </Flx>
          </Flx>
        )}
      </Box>

      {doc && (
        <DiffModal open={diffOpen} before={doc.rows} after={draft} onConfirm={onConfirmSave} onClose={() => setDiffOpen(false)} saving={saving} />
      )}
      {doc && <LockDialog open={lockOpen} onClose={() => setLockOpen(false)} onSubmit={onLock} saving={busy} version={doc.version} />}
      <ChangeHistoryDrawer open={historyOpen} onClose={() => setHistoryOpen(false)} entityType="coupon_ladder" entityId={program} />
    </Box>
  );
}
